/**
 * Research Tasks Hook
 *
 * Manages research task state and progress tracking
 *
 * Primary responsibilities:
 * - Track research tasks dynamically
 * - Parse server events into task updates
 * - Manage task results and progress
 * - Provide state for ResearchTaskTracker component
 *
 * Dependencies:
 * - useServerEvents for SSE integration
 * - useState for state management
 * - useEffect for event listening
 */

'use client'

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'next/navigation'
import { useServerEvents } from '@/hooks/useServerEvents'

// --------------

interface TaskStep {
  id: string
  name: string
  status: 'pending' | 'running' | 'completed' | 'error'
  message?: string
  progress?: number
  results?: TaskResult[]
  startTime?: number
  endTime?: number
}

// --------------

interface TaskResult {
  title: string
  url: string
  snippet?: string
  favicon?: string
}

// --------------

export const useResearchTasks = (chatId?: string | null) => {
  const params = useParams()
  const routeChatId =
    typeof params?.chatid === 'string'
      ? params.chatid
      : Array.isArray(params?.chatid)
        ? params.chatid[0]
        : null
  const scopedChatId = chatId || routeChatId || null
  const [tasks, setTasks] = useState<TaskStep[]>([])
  const [isResearching, setIsResearching] = useState(false)
  const { addEventListener, removeEventListener } = useServerEvents()

  const isDeepResearchEvent = useCallback((event: any) => {
    const key = event?.data?.toolName ?? event?.data?.toolId ?? ''
    return /deepResearch/i.test(key)
  }, [])

  const isScopedEvent = useCallback(
    (event: any) => {
      if (!scopedChatId) {
        return true
      }

      const eventChatId =
        typeof event?.data?.chatId === 'string'
          ? event.data.chatId
          : typeof event?.data?.chat_id === 'string'
            ? event.data.chat_id
            : null

      if (!eventChatId) {
        return false
      }

      return eventChatId === scopedChatId
    },
    [isDeepResearchEvent, scopedChatId]
  )

  // --------------

  const addTask = useCallback((taskData: Partial<TaskStep>) => {
    const newTask: TaskStep = {
      id: taskData.id || `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: taskData.name || 'Research Task',
      status: taskData.status || 'pending',
      message: taskData.message,
      progress: taskData.progress,
      results: taskData.results || [],
      startTime: taskData.startTime || Date.now(),
      ...taskData
    }

    setTasks((prev) => {
      // Check if task already exists
      const existingIndex = prev.findIndex((t) => t.id === newTask.id)
      if (existingIndex >= 0) {
        // Update existing task
        const updated = [...prev]
        updated[existingIndex] = { ...updated[existingIndex], ...newTask }
        return updated
      } else {
        // Add new task
        return [...prev, newTask]
      }
    })
  }, [])

  // --------------

  const updateTask = useCallback((taskId: string, updates: Partial<TaskStep>) => {
    setTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? {
              ...task,
              ...updates,
              endTime: updates.status === 'completed' ? Date.now() : task.endTime
            }
          : task
      )
    )
  }, [])

  // --------------

  const clearTasks = useCallback(() => {
    setTasks([])
    setIsResearching(false)
  }, [])

  // --------------

  const parseSearchResults = useCallback((searchData: any): TaskResult[] => {
    if (!searchData || !searchData.results) return []

    return searchData.results.slice(0, 10).map(
      (result: any): TaskResult => ({
        title: result.title || 'Untitled',
        url: result.url || '#',
        snippet: result.content || result.snippet || '',
        favicon: result.favicon
      })
    )
  }, [])

  // --------------

  const parseProgressEvent = useCallback(
    (eventData: any) => {
      if (!eventData || typeof eventData !== 'object') return

      const { toolId, progress, message, searchResults, dimension, query, error } = eventData

      if (toolId !== 'deepResearch') return

      console.log('[RESEARCH TASKS] Processing event:', {
        toolId,
        progress,
        message,
        dimension,
        query
      })

      // Parse different types of progress messages
      if (message?.includes('Initializing')) {
        clearTasks()
        setIsResearching(true)
        addTask({
          id: 'init',
          name: 'Initializing Research',
          status: 'running',
          message: message,
          progress: progress,
          startTime: Date.now()
        })
      } else if (message?.includes('Phase 1') || message?.includes('discovery')) {
        updateTask('init', { status: 'completed' })
        addTask({
          id: 'discovery',
          name: 'Discovery Phase',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('Agent generated') && message?.includes('discovery queries')) {
        updateTask('discovery', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Discovery search:')) {
        // Add search results to discovery phase
        if (searchResults && searchResults.length > 0) {
          const taskResults = parseSearchResults({ results: searchResults })
          setTasks((prev) =>
            prev.map((task) =>
              task.id === 'discovery'
                ? {
                    ...task,
                    results: [...(task.results || []), ...taskResults].slice(0, 20),
                    message: message
                  }
                : task
            )
          )
        }
      } else if (message?.includes('Agent workforce') && message?.includes('analyzing entity')) {
        addTask({
          id: 'classification',
          name: 'Agent Classification Workforce',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('independent classification agents')) {
        updateTask('discovery', { status: 'completed' })
        addTask({
          id: 'classification',
          name: 'Multi-Agent Classification',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('Classifier') && message?.includes('analyzing entity')) {
        updateTask('classification', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Conflict resolution agent')) {
        updateTask('classification', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Evidence validation agent')) {
        updateTask('classification', {
          message: message,
          progress: progress
        })
      } else if (
        message?.includes('Agent identified') ||
        message?.includes('Agent workforce classified')
      ) {
        updateTask('classification', { status: 'completed' })
        addTask({
          id: 'strategy',
          name: 'Research Strategy Agents',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('research strategy agents') || message?.includes('Strategy')) {
        updateTask('strategy', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Strategy consensus agent')) {
        updateTask('strategy', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Agent workforce created') && message?.includes('dimensions')) {
        updateTask('strategy', {
          status: 'completed',
          message: message
        })
      } else if (message?.includes('analyzing entity')) {
        addTask({
          id: 'analysis',
          name: 'Entity Analysis',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('Dimension')) {
        const match = message.match(/Dimension (\d+)\/(\d+): (.+)/)
        if (match) {
          const [, current, total, dimensionName] = match
          const taskId = `dimension-${current}`
          addTask({
            id: taskId,
            name: dimensionName,
            status: 'running',
            message: `Researching dimension ${current} of ${total}`,
            progress: progress
          })
        }
      } else if (
        message?.includes('Query workforce') ||
        (message?.includes('Agent generated') && message?.includes('queries for'))
      ) {
        // Update the current dimension task with query generation progress
        setTasks((prev) => {
          const lastDimensionTask = prev.find(
            (t) => t.id.startsWith('dimension-') && t.status === 'running'
          )
          if (lastDimensionTask) {
            return prev.map((task) =>
              task.id === lastDimensionTask.id
                ? { ...task, message: message, progress: progress }
                : task
            )
          }
          return prev
        })
      } else if (
        message?.includes('Content enhancement') ||
        message?.includes('content scraping') ||
        message?.includes('Web scraping agents')
      ) {
        addTask({
          id: 'content-enhancement',
          name: 'Content Enhancement',
          status: 'running',
          message: message,
          progress: progress
        })
      } else if (message?.includes('Enhanced content for:')) {
        updateTask('content-enhancement', {
          message: message,
          progress: progress
        })
      } else if (message?.includes('Content enhancement complete')) {
        updateTask('content-enhancement', {
          status: 'completed',
          message: message,
          progress: progress
        })
      } else if (message?.includes('Found') && message?.includes('sources for') && dimension) {
        // Add search results to the specific dimension task
        setTasks((prev) => {
          const dimensionTask = prev.find((t) => t.name === dimension && t.status === 'running')
          if (dimensionTask && searchResults && searchResults.length > 0) {
            const taskResults = parseSearchResults({ results: searchResults })
            return prev.map((task) =>
              task.id === dimensionTask.id
                ? {
                    ...task,
                    results: [...(task.results || []), ...taskResults].slice(0, 25),
                    message: `${query} - ${message.split(' for ')[0]}`
                  }
                : task
            )
          }
          return prev
        })
      } else if (message?.includes('Agent analyzing findings')) {
        // Mark last dimension as completed and start synthesis
        setTasks((prev) => {
          const runningDimensions = prev.filter(
            (t) => t.id.startsWith('dimension-') && t.status === 'running'
          )
          let updatedTasks = prev.map((task) =>
            runningDimensions.some((rd) => rd.id === task.id)
              ? { ...task, status: 'completed' as const }
              : task
          )

          // Add synthesis task if not already added
          if (!updatedTasks.some((t) => t.id === 'synthesis')) {
            updatedTasks.push({
              id: 'synthesis',
              name: 'Analysis & Synthesis',
              status: 'running',
              message: message,
              progress: progress,
              results: [],
              startTime: Date.now()
            })
          }

          return updatedTasks
        })
      } else if (message?.includes('completed')) {
        setIsResearching(false)
        updateTask('synthesis', { status: 'completed' })
      } else if (error) {
        // Handle error messages
        setTasks((prev) => {
          const errorTask = prev.find((t) => t.status === 'running')
          if (errorTask) {
            return prev.map((task) =>
              task.id === errorTask.id
                ? { ...task, status: 'error' as const, message: `Error: ${error}` }
                : task
            )
          }
          return prev
        })
      }
    },
    [addTask, updateTask, clearTasks, parseSearchResults]
  )

  // --------------

  const addSearchResults = useCallback(
    (taskId: string, searchData: any) => {
      const results = parseSearchResults(searchData)
      updateTask(taskId, {
        results: results,
        status: 'completed'
      })
    },
    [parseSearchResults, updateTask]
  )

  // --------------

  // Listen for SSE events
  useEffect(() => {
    const handleStart = (event: any) => {
      if (!isScopedEvent(event)) return
      const key = event?.data?.toolName ?? event?.data?.toolId ?? ''
      if (!/deepResearch/i.test(key)) return
      setIsResearching(true)
      setTasks((prev) => {
        const hasStarter = prev.some((t) => t.id === 'research-start')
        if (hasStarter) return prev
        return [
          {
            id: 'research-start',
            name: 'Deep research',
            status: 'running' as const,
            message: 'Starting research…',
            startTime: Date.now()
          },
          ...prev
        ]
      })
    }

    const handleProgress = (event: any) => {
      if (!isScopedEvent(event)) return
      console.log('[RESEARCH TASKS] SSE Progress event:', event)
      parseProgressEvent(event.data)
    }

    const handleComplete = (event: any) => {
      if (!isScopedEvent(event)) return
      if (!isDeepResearchEvent(event)) return
      console.log('[RESEARCH TASKS] SSE Complete event:', event)
      setIsResearching(false)
      // Mark any running tasks as completed
      setTasks((prev) =>
        prev.map((task) =>
          task.status === 'running' ? { ...task, status: 'completed', endTime: Date.now() } : task
        )
      )
    }

    // Add event listeners for SSE events
    addEventListener('tool.start', handleStart)
    addEventListener('tool.progress', handleProgress)
    addEventListener('tool.complete', handleComplete)

    return () => {
      removeEventListener('tool.start', handleStart)
      removeEventListener('tool.progress', handleProgress)
      removeEventListener('tool.complete', handleComplete)
    }
  }, [
    isDeepResearchEvent,
    isScopedEvent,
    parseProgressEvent,
    addEventListener,
    removeEventListener
  ])

  // --------------

  return {
    tasks,
    isResearching,
    addTask,
    updateTask,
    clearTasks,
    addSearchResults,
    parseSearchResults
  }
}

export default useResearchTasks
